/* =========================================================================
 * pdp-zoom.js — lupa de la galería + gestos del visor de zoom del PDP.
 * Cliente 2026-07-28. Sustituye a pdp-zoom-mobile.js, que se quedó con nombre
 * mentiroso al entrar el comportamiento de escritorio; el viejo ya no lo
 * enlaza nadie y se puede borrar del theme.
 *
 * Qué es de cada sitio:
 *   - LUPA y deslizar-para-pasar-de-foto  → solo móvil (<=749px).
 *   - Cerrar pinchando fuera de la foto   → móvil Y escritorio.
 *   - Matar el zoom por click             → solo móvil; en PC el click amplía.
 * -------------------------------------------------------------------------
 * NO toca assets/main.js. El visor `gallery-viewer` que trae el theme ya hace
 * pinch-zoom, pan, miniaturas y flechas; aquí solo se le añaden dos cosas:
 *
 *   1. LUPA — el botón .pdp-zoom-btn dispara el click del `a.show-gallery`
 *      del slide activo. Así reutiliza el camino de siempre
 *      (MediaGallery.openGalleryViewer), que además abre el visor ya
 *      posicionado en la foto que se estaba viendo (usa su href).
 *
 *   2. DESLIZAR PARA PASAR DE FOTO — en el visor, `touchmove` está enganchado
 *      en el propio <gallery-viewer> y SIEMPRE hace pan. Aquí se intercepta un
 *      escalón antes, en .gallery-viewer__zoom-container, y se corta con
 *      stopPropagation() cuando el gesto debe cambiar de foto. Es el mismo
 *      truco que el theme ya usa para su tira de miniaturas.
 *
 *      Regla (decidida con el cliente): manda el zoom.
 *        - Si la foto NO es más ancha que el visor → no hay nada que recorrer
 *          en horizontal → el deslizamiento PASA DE FOTO.
 *        - Si la foto está ampliada y sobresale → el arrastre la RECORRE por
 *          dentro, como hasta ahora.
 *      El test `img.clientWidth > viewer.clientWidth` es el mismo que usa el
 *      propio theme en panZoomImageFromCoordinate() para decidir si puede
 *      hacer pan, así que no se puede desincronizar de su lógica.
 *
 * Debug: añade `?zoom_debug=1` a la URL.
 * ========================================================================= */
(function () {
  'use strict';

  var MOBILE_MAX = 749;
  var SWIPE_MIN = 40;    /* px de recorrido para que cuente como cambio de foto */
  var DEBUG = /[?&]zoom_debug=1\b/.test(window.location.search);

  function log() {
    if (!DEBUG) return;
    var a = Array.prototype.slice.call(arguments);
    a.unshift('[pdp-zoom]');
    console.log.apply(console, a);
  }

  function isMobile() {
    return window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)').matches;
  }

  /* ---------------- 1. LUPA ---------------- */

  function activeZoomLink(btn) {
    var carousel = btn.closest('carousel-slider');
    if (!carousel) return null;
    /* El slide activo real (los clones del carrusel momentum ya vienen sin
       .show-gallery, así que no pueden colarse aquí). */
    var active = carousel.querySelector('.slider__item.is-active:not([data-clone]) a.show-gallery');
    return active || carousel.querySelector('.slider__item:not([data-clone]) a.show-gallery');
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest ? evt.target.closest('.pdp-zoom-btn') : null;
    if (!btn) return;
    evt.preventDefault();
    var link = activeZoomLink(btn);
    if (!link) { log('sin a.show-gallery en el slide activo'); return; }
    log('lupa -> abriendo visor en', link.getAttribute('href'));
    link.click();
  });

  /* ---------------- 2. DESLIZAR / CERRAR EN EL VISOR ---------------- */

  /* Cierra el visor por el camino del theme (el botón .js-close-modal del
     modal-dialog), en vez de manipular clases a mano. Así el modal se cierra
     igual que con la X y no quedan estados a medias. */
  function closeViewer(viewer) {
    var modal = viewer.closest('modal-dialog');
    var btn = modal && modal.querySelector('.js-close-modal');
    if (btn) { btn.click(); return true; }
    log('no encuentro .js-close-modal');
    return false;
  }

  function wireViewer(viewer) {
    if (!viewer || viewer.__pdpZoomWired) return;
    var zc = viewer.querySelector('.gallery-viewer__zoom-container');
    if (!zc) return;
    viewer.__pdpZoomWired = true;
    log('visor cableado');

    var startX = 0, startY = 0, claimed = false, canPanX = false, justSwiped = false;

    /* ¿Sobresale la foto en horizontal, o sea, hay algo que recorrer?
       OJO: NO vale `img.clientWidth > viewer.clientWidth`. El zoom se aplica con
       `transform: scale()`, que no toca el ancho de LAYOUT: clientWidth es
       siempre el natural de la imagen (5000px), así que ese test daba `true`
       incluso con la foto entera a la vista y el deslizamiento nunca pasaba de
       foto. Hay que usar el MISMO límite que alterCurrentPanBy() del theme:
       panXMax = (naturalWidth * zoom − anchoDelVisor) / 2 ; si es <= 0, no hay
       holgura y el gesto horizontal queda libre para cambiar de foto. */
    function canPanHorizontally() {
      var img = viewer.currentZoomImage;
      if (!img || !img.naturalWidth) return false;
      var z = (viewer.currentTransform && viewer.currentTransform.zoom) || 0;
      return (((img.naturalWidth * z) - viewer.clientWidth) / 2) > 0.5;
    }

    zc.addEventListener('touchstart', function (evt) {
      if (!isMobile() || evt.touches.length !== 1) { claimed = false; return; }
      startX = evt.touches[0].clientX;
      startY = evt.touches[0].clientY;
      claimed = false;
      canPanX = canPanHorizontally();
      log('touchstart canPanX=' + canPanX + ' zoom=' + (viewer.currentTransform && viewer.currentTransform.zoom));
    }, { passive: true });

    zc.addEventListener('touchmove', function (evt) {
      if (!isMobile() || canPanX || evt.touches.length !== 1) return;
      var dx = evt.touches[0].clientX - startX;
      var dy = evt.touches[0].clientY - startY;
      if (!claimed && Math.abs(dx) <= Math.abs(dy)) return;   /* gesto vertical: no es nuestro */
      claimed = true;
      /* Corta el pan del visor: su listener de touchmove está en <gallery-viewer>,
         un escalón por encima de este contenedor. */
      evt.stopPropagation();
    });

    zc.addEventListener('touchend', function (evt) {
      if (!claimed) return;
      claimed = false;
      var t = (evt.changedTouches && evt.changedTouches[0]) || null;
      var dx = t ? t.clientX - startX : 0;
      if (Math.abs(dx) < SWIPE_MIN) { log('deslizamiento corto (' + Math.round(dx) + 'px), se ignora'); return; }
      justSwiped = true;
      setTimeout(function () { justSwiped = false; }, 350);
      if (dx < 0) { log('siguiente foto'); viewer.selectNextThumb(); }
      else { log('foto anterior'); viewer.selectPreviousThumb(); }
    });

    /* CLICK dentro del visor. Hace dos cosas (cliente 2026-07-28):

         a) CERRAR al pulsar FUERA de la foto — en móvil Y en escritorio.
            .gallery-viewer__zoom-container ocupa el 100%×100% de la pantalla,
            así que todo lo que rodea a la foto cae aquí: basta comparar el
            punto con el rect de la imagen. getBoundingClientRect() ya tiene en
            cuenta el transform del zoom.

         b) SOBRE la foto, mata el zoom por toque SOLO EN MÓVIL. El theme tiene
            onZoomContainerClick enganchado al click de este contenedor y salta
            de golpe al zoom nativo ("super zoom"). Se corta en fase de CAPTURA:
            cuando el toque cae sobre la <img>, este listener corre antes que el
            del theme y stopPropagation() impide que el evento burbujee.
            En ESCRITORIO se deja pasar a propósito: allí no hay dos dedos, así
            que el click (con la rueda) es la forma de acercarse, y además la
            foto de la ficha ya lleva el cursor con el `+` anunciándolo. */
    zc.addEventListener('click', function (evt) {
      var img = viewer.currentZoomImage;

      if (img) {
        var r = img.getBoundingClientRect();
        var fuera = evt.clientX < r.left || evt.clientX > r.right ||
                    evt.clientY < r.top  || evt.clientY > r.bottom;
        if (fuera) {
          evt.preventDefault();
          evt.stopPropagation();
          log('click fuera de la foto -> cerrar y volver al PDP');
          closeViewer(viewer);
          return;
        }
      }

      /* A partir de aquí, el click cae SOBRE la foto. */
      if (justSwiped) {
        evt.preventDefault();
        evt.stopPropagation();
        log('click fantasma tras deslizar, ignorado');
        return;
      }

      if (isMobile()) {
        evt.preventDefault();
        evt.stopPropagation();
        log('móvil: el toque sobre la foto no amplía (zoom solo con dos dedos)');
        return;
      }

      log('escritorio: click sobre la foto -> amplía (lo hace el theme)');
    }, true);
  }

  /* El <gallery-viewer> se clona desde un <template> y se inserta al abrirlo,
     así que no existe en el DOM al cargar la página. */
  function scan() { document.querySelectorAll('gallery-viewer').forEach(wireViewer); }

  document.addEventListener('click', function (evt) {
    if (!evt.target.closest) return;
    if (!evt.target.closest('.show-gallery') && !evt.target.closest('.pdp-zoom-btn')) return;
    requestAnimationFrame(function () { requestAnimationFrame(scan); });
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
})();
